import { REFERENCE_TYPE_VALUES } from '@cult/domain';
import { z } from 'zod';
import { idField } from './common';

// Admin-managed reference data (categories, core values, frameworks): one shape for every type —
// a name, slug, optional color, archive flag, and manual sort order.

export const ReferenceTypeSchema = z.enum(REFERENCE_TYPE_VALUES);

export const ReferenceItemSchema = z.object({
  id: idField,
  slug: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  isArchived: z.boolean(),
  sortOrder: z.number().int(),
});
export type ReferenceItem = z.infer<typeof ReferenceItemSchema>;

export const ReferenceListResponseSchema = z.object({
  items: z.array(ReferenceItemSchema),
});
export type ReferenceListResponse = z.infer<typeof ReferenceListResponseSchema>;

// The label a record carries for a reference item it points at (a praise's core values and frameworks).
export const ReferenceEmbedSchema = ReferenceItemSchema.pick({ id: true, slug: true, name: true, color: true });
export type ReferenceEmbed = z.infer<typeof ReferenceEmbedSchema>;

// Reference-item ids a record is tagged with (praise and direct-feedback core values and frameworks).
export const tagIdsField = (max: number, label: string) =>
  z
    .array(z.string().uuid())
    .max(max)
    .refine((ids) => new Set(ids).size === ids.length, `${label} must not repeat`);

export const ReferenceCreateInputSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/, 'slug must be lowercase alphanumeric or hyphen'),
  name: z.string().trim().min(1).max(80),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'color must be a 6-digit hex like #1677ff')
    .nullable()
    .optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type ReferenceCreateInput = z.infer<typeof ReferenceCreateInputSchema>;

export const ReferenceUpdateInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    color: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .nullable()
      .optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
    isArchived: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type ReferenceUpdateInput = z.infer<typeof ReferenceUpdateInputSchema>;
